import { useEffect, useState } from "react";
import {
  Box,
  TableCell,
  TableRow,
  IconButton,
  Icon,
  Tooltip,
  Modal,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { ENVIRONMENT } from "../../environment/environment";
import { useNavigate } from "react-router-dom";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { useDebounce } from "../../shared/utils/useDebounce/use-debounce";
import { toastError } from "../../shared/services/errors/erros-handler";
import { caixaDeEntradaService } from "./service/caixa-de-entrada.service";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import { toast } from "react-toastify";
import { formatarTelefone, formatDateBR } from "../../shared/utils";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisualizarMensagem from "./visualizar-mensagem";

interface CaixaDeEntradaItem {
  id: string;
  idCompany: string;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  dateMessage: string;
  view: boolean;
}

export function ListagemCaixaDeEntrada() {
  const [mensagens, setMensagens] = useState<CaixaDeEntradaItem[]>([]);
  const [busca, setBusca] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [modalFiltroAberto, setModalFiltroAberto] = useState(false);
  const [dataFiltro, setDataFiltro] = useState("");
  const [modalVisualizar, setModalVisualizar] = useState(false);
  const [mensagemSelecionada, setMensagemSelecionada] = useState<CaixaDeEntradaItem | null>(null);

  const buscaDebounce = useDebounce(busca, 500);
  const navigate = useNavigate();

  const carregarMensagens = async (
    filtro?: string,
    pagina = page,
    limite = rowsPerPage,
    dataFiltroExata?: string
  ) => {
    try {
      setIsLoading(true);
      // Passa a data exata como campo de pesquisa se fornecida
      let campoPesquisa: string | undefined = undefined;
      let valorPesquisa: string | undefined = undefined;
      if (dataFiltroExata) {
        campoPesquisa = "dateMessage";
        valorPesquisa = dataFiltroExata;
      }
      const result = await caixaDeEntradaService.findAll(
        filtro,
        pagina,
        limite,
        campoPesquisa,
        valorPesquisa
      );
      setMensagens(result.items || []);
      setTotalCount(result.totalItems || 0);
    } catch (error) {
      setMensagens([]);
      toastError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    carregarMensagens(undefined, 0);
  }, []);

  useEffect(() => {
    setPage(0);
    carregarMensagens(buscaDebounce, 0);
  }, [buscaDebounce]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    carregarMensagens(buscaDebounce, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    carregarMensagens(buscaDebounce, 0, newLimit);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Tem certeza que deseja excluir esta mensagem?")) {
      try {
        const msgDeletada = await caixaDeEntradaService.deletar(id);
        toast.success(msgDeletada);
        carregarMensagens(buscaDebounce, page, rowsPerPage);
      } catch (error) {
        toastError(error);
      }
    }
  };

  const handleFiltrarPorData = () => {
    setModalFiltroAberto(false);
    setPage(0);
    carregarMensagens(buscaDebounce, 0, rowsPerPage, dataFiltro);
  };

  return (
    <BaseLayout
      title="Caixa de Entrada"
      barraDeFerramentas={
        <FerramanetasDaListagem
          placeholder={ENVIRONMENT.INPUT_DE_BUSCA + " por nome, email, assunto..."}
          textoBotaoNovo="Filtrar por Data"
          mostrarInputBusca
          textoDaBusca={busca}
          aoMudarTextoDeBusca={setBusca}
          aoClicarBotaoNovo={() => setModalFiltroAberto(true)}
        />
      }
    >
      <Box display="flex" flexDirection="column" paddingBottom="40px">
        <PaginacaoListagem
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />

        <TabelaDaListagem
          ariaLabel="tabela de mensagens"
          headTable={[
            "Nome",
            "Email",
            "Telefone",
            "Assunto",
            "Data",
            "Ações",
          ]}
          columnWidths={[180, 220, 120, 300, 160, 120]}
          tableBody={
            !isLoading && Array.isArray(mensagens) && mensagens.length > 0 ? (
              mensagens.map((msg) => (
                <TableRow hover key={msg.id}>
                  <TableCell>{msg.name}</TableCell>
                  <TableCell>{msg.email}</TableCell>
                  <TableCell>{formatarTelefone(msg.phone)}</TableCell>
                  <TableCell>{msg.subject}</TableCell>
                  <TableCell>{formatDateBR(msg.dateMessage)}</TableCell>
                  <TableCell>
                    <Tooltip title="Visualizar Mensagem">
                      <IconButton size="small" onClick={() => {
                        setMensagemSelecionada(msg);
                        setTimeout(() => setModalVisualizar(true), 0);
                      }}>
                        <VisibilityIcon color={msg.view ? "primary" : "inherit"} />
                      </IconButton>
                    </Tooltip>
                    {msg.view && (
                      <Tooltip title="Excluir Mensagem">
                        <IconButton size="small" onClick={() => handleDelete(msg.id)}>
                          <Icon>delete</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {isLoading ? "Carregando..." : "Nenhuma mensagem encontrada :("}
                </TableCell>
              </TableRow>
            )
          }
        />
      </Box>
      <Modal open={modalFiltroAberto} onClose={() => setModalFiltroAberto(false)}>
        <Box
          p={4}
          borderRadius={2}
          boxShadow={24}
          minWidth={350}
          maxWidth={400}
          mx="auto"
          mt="10vh"
          display="flex"
          flexDirection="column"
          gap={2}
          bgcolor="background.paper"
          color="text.primary"
        >
          <Typography variant="h6">Filtrar por Data</Typography>
          <TextField
            label="Data"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={dataFiltro}
            onChange={(e) => setDataFiltro(e.target.value)}
          />
          <Button variant="contained" onClick={handleFiltrarPorData}>
            Filtrar
          </Button>
        </Box>
      </Modal>

      {/* Modal Visualizar Mensagem */}
      {mensagemSelecionada && (
        <Modal open={modalVisualizar} onClose={() => setModalVisualizar(false)}>
          <VisualizarMensagem
            mensagem={mensagemSelecionada}
            onClose={() => {
              setModalVisualizar(false);
              window.location.reload();
            }}
          />
        </Modal>
      )}
    </BaseLayout>
  );
}
